import type { Point, Rectangle } from "./geom";

/**
 * flash.display.BitmapData backed by a 2d canvas.
 *
 * Asset-backed instances start out wrapping the decoded <img> and only
 * materialise a canvas when something writes into them, so the ~190 sheet
 * bitmaps never pay for a copy.
 *
 * Every mutator leaves the context at `source-over` with `globalAlpha` 1, so
 * the hot paths below never have to reset it: `copyPixels` runs hundreds of
 * times per frame while the level bitmaps are rebuilt.
 */
export class BitmapData {
  readonly width: number;
  readonly height: number;
  readonly transparent: boolean;

  /** Bumped on every mutation so dependent caches (tints, patterns) invalidate. */
  version = 0;

  private image: CanvasImageSource | null = null;
  private canvas: HTMLCanvasElement | null = null;
  private context: CanvasRenderingContext2D | null = null;

  constructor(
    width: number,
    height: number,
    transparent = true,
    fillColor = 0xffffffff,
    image?: CanvasImageSource,
  ) {
    this.width = Math.max(1, width | 0);
    this.height = Math.max(1, height | 0);
    this.transparent = transparent;
    if (image) {
      this.image = image;
    } else if (!transparent || fillColor >>> 24 !== 0) {
      const ctx = this.ctx;
      ctx.fillStyle = argbToCss(
        transparent ? fillColor : fillColor | 0xff000000,
      );
      ctx.fillRect(0, 0, this.width, this.height);
    }
  }

  /** What a renderer should draw. Never null once constructed. */
  get source(): CanvasImageSource {
    return this.canvas ?? this.image ?? this.ctx.canvas;
  }

  /** Writable backing store; promotes an image-backed bitmap to a canvas. */
  get ctx(): CanvasRenderingContext2D {
    if (this.context) return this.context;
    const canvas = document.createElement("canvas");
    canvas.width = this.width;
    canvas.height = this.height;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("2d context unavailable");
    ctx.imageSmoothingEnabled = false;
    if (this.image) ctx.drawImage(this.image, 0, 0);
    this.canvas = canvas;
    this.context = ctx;
    return ctx;
  }

  /**
   * Replaces the destination region — flash copyPixels overwrites alpha rather
   * than blending, so the target rect is cleared first.
   */
  copyPixels(
    source: BitmapData,
    sourceRect: Rectangle,
    destPoint: Point,
    alphaBitmapData?: BitmapData | null,
    alphaPoint?: Point | null,
    mergeAlpha = false,
  ): void {
    const ctx = this.ctx;
    const dx = Math.round(destPoint.x);
    const dy = Math.round(destPoint.y);
    const sw = Math.round(sourceRect.width);
    const sh = Math.round(sourceRect.height);
    if (sw <= 0 || sh <= 0) return;
    if (!mergeAlpha) ctx.clearRect(dx, dy, sw, sh);
    ctx.drawImage(
      source.source,
      Math.round(sourceRect.x),
      Math.round(sourceRect.y),
      sw,
      sh,
      dx,
      dy,
      sw,
      sh,
    );
    if (alphaBitmapData) {
      ctx.save();
      ctx.beginPath();
      ctx.rect(dx, dy, sw, sh);
      ctx.clip();
      ctx.globalCompositeOperation = "destination-in";
      const ax = Math.round(alphaPoint?.x ?? 0);
      const ay = Math.round(alphaPoint?.y ?? 0);
      ctx.drawImage(alphaBitmapData.source, ax, ay, sw, sh, dx, dy, sw, sh);
      ctx.restore();
    }
    this.version++;
  }

  fillRect(rect: Rectangle, color: number): void {
    const ctx = this.ctx;
    if (this.transparent && color >>> 24 === 0) {
      ctx.clearRect(rect.x, rect.y, rect.width, rect.height);
    } else {
      // copy so the fill replaces alpha instead of blending, as flash does.
      ctx.globalCompositeOperation = "copy";
      ctx.fillStyle = argbToCss(this.transparent ? color : color | 0xff000000);
      ctx.fillRect(rect.x, rect.y, rect.width, rect.height);
      ctx.globalCompositeOperation = "source-over";
    }
    this.version++;
  }

  // Flash batches pixel writes between lock/unlock; canvas needs no equivalent.
  lock(): void {}
  unlock(): void {}
}

function argbToCss(argb: number): string {
  const a = ((argb >>> 24) & 0xff) / 255;
  return `rgba(${(argb >>> 16) & 0xff},${(argb >>> 8) & 0xff},${argb & 0xff},${a})`;
}
